import React from 'react'

import { useLocation } from 'react-router-dom'

import { COMMUNICATION_MODE_HUBSPOT } from '@constants'

import { useCommunicationMode } from './useCommunicationMode'

const SCRIPT_ID = 'hs-script-loader'

/**
 * The embed code's `src`, verbatim from HubSpot
 */
function getScriptSrc(): Nullable<string> {
  const src = window.hubspotScriptSrc
  // an unsubstituted placeholder means the environment has no portal configured
  if (!src || src.startsWith('%')) {
    return null
  }
  // HubSpot hands out a protocol-relative URL; the app is https-only
  return src.startsWith('//') ? `https:${src}` : src
}

/**
 * Loads HubSpot for hubspot-mode companies and keeps it told which page the user is on.
 *
 * The communication mode decides who gets a chat at all; HubSpot's chatflow targeting then decides
 * which of their pages show the bubble. A company on another channel never loads the script, and one
 * that switches away from hubspot mid-session has the widget taken off the page.
 */
export function useHubSpotChat() {
  const { pathname, search } = useLocation()
  const communicationMode = useCommunicationMode()
  const isHubSpotCompany = communicationMode === COMMUNICATION_MODE_HUBSPOT

  React.useEffect(() => {
    const src = getScriptSrc()
    if (!src) {
      return
    }

    if (!isHubSpotCompany) {
      // the script cannot be unloaded, so a company switch relies on the widget removing itself
      window.HubSpotConversations?.widget?.remove?.()
      return
    }

    if (document.getElementById(SCRIPT_ID)) {
      // back on a hubspot company after a switch: the script is still here, the widget is not
      window.HubSpotConversations?.widget?.load?.()
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = src
    document.body.appendChild(script)
  }, [isHubSpotCompany])

  React.useEffect(() => {
    if (!getScriptSrc() || !isHubSpotCompany) {
      return
    }

    const hsq = (window._hsq = window._hsq || [])
    hsq.push(['setPath', pathname + search])
    hsq.push(['trackPageView'])
    // The widget picks its targeting rules when it loads, and a client-side navigation is invisible to
    // it - without this the bubble outlives the pages it is meant for, or never appears at all.
    window.HubSpotConversations?.widget?.refresh?.()
  }, [isHubSpotCompany, pathname, search])
}
